import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Generic multi-select for lists (CO-0048): click toggles one row, shift+click selects the range from the last
 * clicked row, and "all" selects every row on the page. Pure state, no data fetching; the list decides the actions.
 */
export interface Selection<K> {
  selected: ReadonlySet<K>;
  count: number;
  isSelected(key: K): boolean;
  /** Toggle a row; with range = true (shift) it selects from the anchor to this row. */
  toggle(key: K, range?: boolean): void;
  allSelected: boolean;
  someSelected: boolean;
  toggleAll(): void;
  clear(): void;
}

export function useSelection<K>(keys: readonly K[]): Selection<K> {
  const [selected, setSelected] = useState<ReadonlySet<K>>(new Set());
  const anchor = useRef<K | null>(null);

  // Drop selected keys that are no longer on the page.
  const visible = useMemo(() => new Set(keys), [keys]);
  const effective = useMemo(() => new Set([...selected].filter((k) => visible.has(k))), [selected, visible]);

  const toggle = useCallback(
    (key: K, range = false) => {
      setSelected((prev) => {
        const next = new Set([...prev].filter((k) => visible.has(k)));
        const from = anchor.current;
        if (range && from !== null && visible.has(from)) {
          const a = keys.indexOf(from);
          const b = keys.indexOf(key);
          const [lo, hi] = a < b ? [a, b] : [b, a];
          const on = !prev.has(key);
          for (const k of keys.slice(lo, hi + 1)) {
            if (on) next.add(k);
            else next.delete(k);
          }
        } else if (next.has(key)) next.delete(key);
        else next.add(key);
        anchor.current = key;
        return next;
      });
    },
    [keys, visible],
  );

  const allSelected = keys.length > 0 && keys.every((k) => effective.has(k));
  return {
    selected: effective,
    count: effective.size,
    isSelected: (k) => effective.has(k),
    toggle,
    allSelected,
    someSelected: effective.size > 0 && !allSelected,
    toggleAll: () => setSelected(allSelected ? new Set() : new Set(keys)),
    clear: () => setSelected(new Set()),
  };
}
